import React, {useState} from "react"
import {Body, H5} from "@irati/ui/src/Typography"
import Validation from "@irati/utils/validation"
import FormattedMessage, {useIntl} from "@irati/utils/lang"
import Card from "@irati/ui/src/Layout/Card"
import {AutomationRewardType, RecognitionAutomationRule, RecognitionAutomationRuleInput, RecognitionAutomationRuleType} from "@irati/types"
import Flex from "@irati/ui/src/Flex"
import mainTheme from "@irati/ui/src/global/theme"
import {Input, Select} from "@irati/ui/src/FormItem"
import Icon, {IconTypes} from "@irati/ui/src/Icon"
import Spacing from "@irati/ui/src/Spacing"
import {SelectOption} from "@irati/ui/src/FormItem/Select"
import Button, {IconButton} from "@irati/ui/src/Button"
import CardOption from "./CardOptions"
import {YearOptionContainer} from "./styled"

function pointsPerUserSchema() {
  return Validation.string("platform.defaultValidation.required").test(
    "Positive, value above 1 and no decimal",
    "platform.validation.number.nonZeroValue",
    (val) => val > 0
  )
}
interface Props {
  rules: RecognitionAutomationRuleInput[]
  ruleType: RecognitionAutomationRuleType
  automationRewardType: AutomationRewardType
  onRuleTypeChange: (ruleType: RecognitionAutomationRuleType) => void
  onRulesChange: (rules: RecognitionAutomationRule[]) => void
}
type YearsSettingRow = {
  years: number[]
  amount?: number
}
const YearsArray = Array.from({length: 40}, (_, index) => index + 1)
const mapSettingsToRules = (settings: YearsSettingRow[]): RecognitionAutomationRule[] => {
  return settings
    .map((setting) => {
      return setting.years.map<RecognitionAutomationRule>((year) => ({
        year,
        amount: parseInt(setting.amount)
      }))
    })
    .flat()
}
const mapRulesToSettings = (rules: RecognitionAutomationRuleInput[]): YearsSettingRow[] => {
  if (!rules?.length) {
    return [
      {
        years: []
      }
    ]
  }
  const groupYearsPoints = rules?.reduce((acc, item) => {
    const prevYears = acc[item.amount!] || []
    return {
      ...acc,
      [item.amount!]: [...prevYears, item.year]
    }
  }, {} as Record<number, number[]>)
  return Object.entries(groupYearsPoints).map(([amount, years]) => ({
    years,
    amount
  }))
}
const AutomationsRules = (props: Props) => {
  const {rules, onRulesChange, automationRewardType, onRuleTypeChange, ruleType} = props
  const intl = useIntl()
  const isYearly = ruleType === RecognitionAutomationRuleType.Yearly
  const isCustom = ruleType === RecognitionAutomationRuleType.Custom
  const [yearsSettings, setYearsSettings] = useState(mapRulesToSettings(rules))
  const handleTypeChange = (type: RecognitionAutomationRuleType) => {
    setYearsSettings([
      {
        years: []
      }
    ])
    onRuleTypeChange(type)
  }
  const handlePointsPerUser = (amount: string) => {
    onRulesChange([
      {
        amount: parseInt(amount)
      }
    ])
  }
  const handleYearAmount = (amount: string, block: number) => {
    const newRules = Array.from(yearsSettings)
    newRules[block].amount = parseInt(amount)
    setYearsSettings(newRules)
    onRulesChange(mapSettingsToRules(newRules))
  }
  const handleYearSelect = (years: SelectOption[], block: number) => {
    const newYears = years.map(({value}) => value)
    newYears.sort()
    const newRules = Array.from(yearsSettings)
    newRules[block].years = newYears
    setYearsSettings(newRules)
    onRulesChange(mapSettingsToRules(newRules))
  }
  const addYearsRow = (e) => {
    e.preventDefault()
    setYearsSettings([
      ...yearsSettings,
      {
        years: []
      }
    ])
  }
  const removeRow = (index: number) => {
    const newYearsSettings = Array.from(yearsSettings)
    newYearsSettings.splice(index, 1)
    setYearsSettings(newYearsSettings)
    onRulesChange(mapSettingsToRules(newYearsSettings))
  }
  const yearsSelect = YearsArray.filter((val) => !rules?.some((rule) => rule.year === val)).map((year) => ({
    label: intl.formatMessage({id: "admin.automation.lbl.customYears"}, {years: year}),
    value: year
  }))
  return (
    <>
      <Body small>
        <FormattedMessage id="admin.automation.rewardRulesSubtitle" />
      </Body>
      {automationRewardType === AutomationRewardType.FixedCustom && (
        <Flex gap={2} marginTop={mainTheme.spacing.oneAndHalf}>
          <CardOption full selected={isYearly} onSelect={() => handleTypeChange(RecognitionAutomationRuleType.Yearly)}>
            <Body small weight={isYearly ? "bold" : "default"}>
              <FormattedMessage id="admin.automation.create.rules.title.yearly" />
            </Body>
          </CardOption>
          <CardOption full selected={isCustom} onSelect={() => handleTypeChange(RecognitionAutomationRuleType.Custom)}>
            <Body small weight={isCustom ? "bold" : "default"}>
              <FormattedMessage id="admin.automation.create.rules.title.custom" />
            </Body>
          </CardOption>
        </Flex>
      )}
      {isYearly && (
        <Spacing marginTop={mainTheme.spacing.oneAndHalf}>
          <Input
            name="pointsPerUser"
            type="number"
            min="1"
            addon={{
              before: <Icon icon={IconTypes.rewardPoints} />
            }}
            flush
            validation={pointsPerUserSchema()}
            value={String(rules[0]?.amount)}
            placeholder="platform.automation.distribute.enterPoints.placeholder"
            onChange={handlePointsPerUser}
          />
        </Spacing>
      )}
      {isCustom && (
        <Spacing marginTop={mainTheme.spacing.oneAndHalf}>
          <Card small bgColor={mainTheme.colors.primary1} noBorder>
            <Flex gap={mainTheme.spacing.one} alignItems="center">
              <Icon icon={IconTypes.info} color={mainTheme.colors.utility.white} />
              <H5 color={mainTheme.colors.utility.white} display="inline">
                <FormattedMessage id="admin.automation.msg.recognitionNoReward" />
              </H5>
            </Flex>
          </Card>
          {yearsSettings.map(({amount, years}, index) => (
            <Flex key={index} alignItems="center" gap={2}>
              <YearOptionContainer gap={1} alignItems="flex-start" flex="1">
                <Select
                  isClearable
                  isMulti
                  flush
                  value={years.map((yearOption) => ({
                    value: yearOption,
                    label: intl.formatMessage({id: "admin.automation.lbl.customYears"}, {years: yearOption})
                  }))}
                  options={yearsSelect}
                  validation={Validation.string(intl.formatMessage({id: "platform.defaultValidation.required"}))}
                  onChange={(val: SelectOption[]) => handleYearSelect(val, index)}
                />
                <Input
                  type="number"
                  min="1"
                  disabled={!years.length}
                  addon={{
                    before: <Icon icon={IconTypes.rewardPoints} />
                  }}
                  flush
                  validation={pointsPerUserSchema()}
                  value={String(amount)}
                  placeholder="platform.automation.distribute.enterPoints.placeholder"
                  onChange={(val) => handleYearAmount(val, index)}
                />
              </YearOptionContainer>
              {yearsSettings.length > 1 && (
                <IconButton
                  large
                  color={mainTheme.colors.greys.grey50}
                  icon={IconTypes.delete}
                  onClick={() => removeRow(index)}></IconButton>
              )}
            </Flex>
          ))}
          <Button cta onClick={addYearsRow} small marginTop={1}>
            <Flex gap={1} alignItems="center">
              <Icon icon={IconTypes.plus} small />
              <FormattedMessage id="admin.automation.cta.addRule" />
            </Flex>
          </Button>
        </Spacing>
      )}
    </>
  )
}

export default AutomationsRules
